import { compareBoardColumns, compareSortKeys } from '@aline/common';
import { Apollo } from 'src/gql/generated';

type ColumnPositionInput = Pick<Apollo.Column, 'category' | 'id' | 'sortKey'>;
type BoardColumn = Pick<Apollo.Column, 'category' | 'id' | 'name' | 'sortKey'>;

/** Position of a column within its category group (1-based index). */
export function getColumnPositionInCategory(
  column: ColumnPositionInput,
  columns: ColumnPositionInput[]
): { index: number; total: number } {
  const categoryColumns = columns
    .filter(option => option.category === column.category)
    .sort((columnA, columnB) => compareSortKeys(columnA.sortKey, columnB.sortKey));

  return {
    index: categoryColumns.findIndex(option => option.id === column.id) + 1,
    total: categoryColumns.length,
  };
}

export interface WorkflowPhaseColumnHint {
  column: Pick<Apollo.Column, 'id'>;
  id: string;
  name: string;
  completedAt?: string | null;
  order?: number;
  startedAt?: string | null;
}

export function getStartedColumns<TColumn extends BoardColumn>(columns: TColumn[]): TColumn[] {
  return columns
    .filter(column => column.category === Apollo.ColumnCategory.Started)
    .sort((a, b) => compareSortKeys(a.sortKey, b.sortKey));
}

export function getNonSigningStartedColumns<TColumn extends BoardColumn>(
  columns: TColumn[],
  signingColumnId: string | null | undefined
): TColumn[] {
  return getStartedColumns(columns).filter(column => column.id !== signingColumnId);
}

export function getDoneColumns<TColumn extends BoardColumn>(columns: TColumn[]): TColumn[] {
  return columns
    .filter(column => column.category === Apollo.ColumnCategory.Done)
    .sort((a, b) => compareSortKeys(a.sortKey, b.sortKey));
}

/**
 * The columns a phase may call home: the board's Signing column for a signing phase, every
 * non-signing Started column otherwise.
 *
 * `currentColumn` is always included, even when it falls outside that set — a home stamped
 * before the column was recategorized, or one the board has since reshuffled. Dropping it would
 * leave the picker unable to show the user where the phase currently lives, and on a board whose
 * only other Started column is Signing it would leave the picker empty.
 */
export function getPhaseHomeColumnOptions<TColumn extends BoardColumn>(args: {
  columns: TColumn[];
  isSigningPhase: boolean;
  signingColumnId: string | null | undefined;
  currentColumn?: TColumn;
}): TColumn[] {
  const { columns, currentColumn, isSigningPhase, signingColumnId } = args;
  const options = isSigningPhase
    ? columns.filter(column => column.id === signingColumnId)
    : getNonSigningStartedColumns(columns, signingColumnId);
  if (!currentColumn || options.some(column => column.id === currentColumn.id)) return options;

  return [...options, currentColumn].sort(compareBoardColumns);
}

export function getIsPausedOrCancelledCategory(category: Apollo.ColumnCategory): boolean {
  return category === Apollo.ColumnCategory.Paused || category === Apollo.ColumnCategory.Cancelled;
}

export function getNeedsUnparkConfirm(
  currentCategory: Apollo.ColumnCategory | undefined,
  targetCategory: Apollo.ColumnCategory | undefined
): boolean {
  if (!currentCategory || !targetCategory) return false;
  return (
    getIsPausedOrCancelledCategory(currentCategory) &&
    !getIsPausedOrCancelledCategory(targetCategory)
  );
}

export function getActivePhase<TPhase extends WorkflowPhaseColumnHint>(
  phases: TPhase[]
): TPhase | undefined {
  const ordered = [...phases].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return ordered.find(phase => Boolean(phase.startedAt) && !phase.completedAt);
}

export function getSingleColumnId(columns: BoardColumn[]): string | null {
  if (columns.length !== 1) return null;
  return columns.at(0)?.id ?? null;
}

/**
 * Best guess for the column a workflow should use on another board: the column with the same
 * name, else the only option there is. Boards copied from each other share column names, so
 * this fills in the whole mapping for the common case and leaves the rest to the user.
 */
export function getMatchingColumnId(
  sourceColumn: BoardColumn | undefined,
  targetColumns: BoardColumn[]
): string | null {
  const sourceName = sourceColumn?.name.trim().toLowerCase();
  const nameMatch = sourceName
    ? targetColumns.find(column => column.name.trim().toLowerCase() === sourceName)
    : undefined;

  return nameMatch?.id ?? getSingleColumnId(targetColumns);
}

export interface RelocationPhase extends WorkflowPhaseColumnHint {
  __typename?: string;
}

export function getIsSigningRelocationPhase(phase: RelocationPhase): boolean {
  switch (phase.__typename) {
    case 'SigningPhase':
      return true;
    case 'NonSigningPhase':
      return false;
    default:
      return false;
  }
}

export function getDistinctNonSigningPhaseColumnIds(phases: RelocationPhase[]): string[] {
  const columnIds = new Set<string>();
  for (const phase of phases) {
    if (getIsSigningRelocationPhase(phase)) continue;
    columnIds.add(phase.column.id);
  }
  return [...columnIds];
}

export function getFirstPhaseColumnId(phases: RelocationPhase[]): string | null {
  const ordered = [...phases].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return ordered.at(0)?.column.id ?? null;
}

export function getTriageColumnId(columns: BoardColumn[]): string | null {
  const triageColumn = columns.find(column => column.category === Apollo.ColumnCategory.Triage);
  return triageColumn?.id ?? null;
}

export interface PhaseColumnRemap {
  fromColumnId: string;
  toColumnId: string;
}

export function buildPhaseColumnRemaps(args: {
  phaseRemapSelections: Record<string, string>;
  phases: RelocationPhase[];
}): PhaseColumnRemap[] {
  const remaps: PhaseColumnRemap[] = [];
  const { phaseRemapSelections, phases } = args;

  for (const fromColumnId of getDistinctNonSigningPhaseColumnIds(phases)) {
    const toColumnId = phaseRemapSelections[fromColumnId];
    if (toColumnId) remaps.push({ fromColumnId, toColumnId });
  }

  return remaps;
}

export function getDefaultRelocationColumnId(args: {
  phaseRemapSelections: Record<string, string>;
  phases: RelocationPhase[];
  startedAt: string | null | undefined;
  targetColumns: BoardColumn[];
}): string | null {
  const { phaseRemapSelections, phases, startedAt, targetColumns } = args;
  const mappedFirstPhaseColumnId = getRemappedColumnId(
    phaseRemapSelections,
    getFirstPhaseColumnId(phases)
  );
  if (!startedAt) return getTriageColumnId(targetColumns) ?? mappedFirstPhaseColumnId;

  const activePhase = getActivePhase(phases);
  if (!activePhase) return mappedFirstPhaseColumnId;

  return (
    getRemappedColumnId(phaseRemapSelections, activePhase.column.id) ?? mappedFirstPhaseColumnId
  );
}

/**
 * A phase the user hasn't mapped yet is held as an empty string, so every read has to
 * normalize it back to null — otherwise it satisfies `??` and swallows the next fallback.
 */
function getRemappedColumnId(
  phaseRemapSelections: Record<string, string>,
  fromColumnId: string | null
): string | null {
  if (!fromColumnId) return null;
  return phaseRemapSelections[fromColumnId] || null;
}
